import * as React from 'react';
import { useState, useEffect } from 'react';
import { Alert, Box, Snackbar, Tab, Tabs } from '@mui/material';
import { withTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import ExportButtons from '../components/ExportButtons';
import InventoryValueSection from './InventoryValueSection';
import InventoryMovementSection from './InventoryMovementSection';

/**
 * Varasto tab: what the stock was worth at the end of the chosen month, and the
 * movements that got it there. Split into sub-tabs rather than stacked so the
 * movement list, which can run to thousands of rows, is only fetched when asked
 * for.
 */
function InventoryTab({ t, year, month }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [subTab, setSubTab] = useState(() =>
    searchParams.get('subtab') === 'movements' ? 1 : 0
  );
  const [exportError, setExportError] = useState(null);

  useEffect(() => {
    const next = searchParams.get('subtab') === 'movements' ? 1 : 0;
    if (next !== subTab) {
      setSubTab(next);
    }
  }, [searchParams]);

  const handleSubTabChange = (event, newValue) => {
    setSubTab(newValue);
    const newParams = { tab: 'inventory' };
    if (newValue === 1) {
      newParams.subtab = 'movements';
    }
    setSearchParams(newParams, { replace: true });
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Tabs
          value={subTab}
          onChange={handleSubTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 'auto',
              px: 2,
            },
          }}
        >
          <Tab
            label={t('reports.inventory.value')}
            data-testid="reports-inventory-subtab-value"
          />
          <Tab
            label={t('reports.inventory.movements')}
            data-testid="reports-inventory-subtab-movements"
          />
        </Tabs>
        <ExportButtons
          reportType="inventory"
          params={{ year, month, section: subTab === 1 ? 'movement' : 'value' }}
          testIdPrefix={
            subTab === 1 ? 'reports-inventory-movements-export' : 'reports-inventory-value-export'
          }
          onError={() => setExportError(t('reports.inventory.exportFailed'))}
        />
      </Box>

      {subTab === 1 ? (
        <InventoryMovementSection year={year} month={month} />
      ) : (
        <InventoryValueSection year={year} month={month} />
      )}

      <Snackbar
        open={Boolean(exportError)}
        autoHideDuration={6000}
        onClose={() => setExportError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setExportError(null)} sx={{ width: '100%' }}>
          {exportError}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(InventoryTab);
